import _ from 'lodash';
import { useLocation } from 'react-router-dom';
import { useState, useEffect, useContext, createContext, useCallback } from 'react';
import GenericCheckbox from '../../../controls/GenericCheckbox';
import GenericDropdown from '../../../controls/GenericDropdown';
import GenericTextBox from '../../../controls/GenericTextBox';
import SortableObjectComponent from './propEditorComponents/SortableObjectComponent';
import CustomKeyObject from './propEditorComponents/CustomKeyObject';
import ObjectComponent from './propEditorComponents/ObjectComponent';
import LayoutItemListRenderer from './propEditorComponents/LayoutItemListRenderer';
import CssComponent from './propEditorComponents/CssComponent';
import BUILD_TIME_CONFIG_TYPES from '../../../../config/buildTimeConfigTypes';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Utils from '../../../../utils/utils.js';

const UIConfigContext = createContext({});

export function UIConfigProvider({ buildTimeConfig, templateConfig, templateMetadata, onSave, onLivePreviewUpdate, canSendUpdate, children, }) {
  const { state } = useLocation();
  const [isSendingUpdates, setIsSendingUpdates] = useState(false);

  const [basePath, setBasePath] = useState([]);

  const [allConfig] = useState(_.cloneDeep(buildTimeConfig));
  const [template] = useState(JSON.parse(templateConfig.config['/config.json']));
  const [overrides] = useState(JSON.parse(allConfig.config['/config/placeholder.json']));

  const templateBCs = JSON.parse(templateConfig.bcs['/init/bc.json']);
  const overridesBCs = JSON.parse(allConfig.bcs['/init/placeholder.json']);

  const [bcs] = useState(_.merge({}, templateBCs, overridesBCs));

  const templateDPs = JSON.parse(templateConfig.dps['/init/dp.json']);
  const overridesDPs = JSON.parse(allConfig.dps['/init/placeholder.json']);

  const [dps] = useState(_.merge({}, templateDPs, overridesDPs));

  const templateSchemas = JSON.parse(templateConfig.schema['/init/schema.json']);
  const overridesSchemas = JSON.parse(allConfig.schema['/init/placeholder.json']);

  const [schemas] = useState(_.merge({}, templateSchemas, overridesSchemas));

  const bcFieldMappings = {};
  Object.values(bcs).forEach((bc) => {
    bcFieldMappings[bc.name] = [];

    const dp = dps[bc.config.dataProviderName];
    const schema = schemas[dp.config.schemaId || templateMetadata.defaultSchemaId];

    dp.entities.forEach(({ name, primary, }) => {
      schema[name]?.Attributes.forEach(({ Name }) => {
        bcFieldMappings[bc.name].push(`${primary ? '' : `${name}_`}${Name}`);
      });
    });
  });

  const [bcFields] = useState(bcFieldMappings);

  // * Maintain overrides separately to update the json depending on whether config comes from overrides or the template
  const [currentOverrides, setCurrentOverrides] = useState(_.cloneDeep(overrides));

  // * Contain the merged data for displaying in the UI
  const [pages, setPages] = useState({});
  const [applets, setApplets] = useState({});
  const [blocks, setBlocks] = useState({});
  const [components, setComponents] = useState({});

  const [selectedLayoutItem, setSelectedLayoutItem] = useState(null);
  const [selectedLayoutItemType, setSelectedLayoutItemType] = useState(null);
  const [drillDownLayoutItem, setDrillDownLayoutItem] = useState(null);
  const [drillDownLayoutItemType, setDrillDownLayoutItemType] = useState(null);

  const [propEditorPath, setPropEditorPath] = useState([]);
  const [propOverrides, setPropOverrides] = useState(null);
  const [propConfigOverrides, setPropConfigOverrides] = useState(null);
  const [outerBc, setOuterBc] = useState(null);
  const [hasTemplateBc, setHasTemplateBc] = useState(false);
  const [bcContext, setBcContext] = useState(null);
  const [currentBcContext, setCurrentBcContext] = useState(null);
  const [showAdvancedProps, setShowAdvancedProps] = useState(false);
  const [valuePrompterData, setValuePrompterData] = useState({});
  const [translate] = useTranslation();

  const reservedArgs = ['BLOCK', 'LAYOUTITEM', 'path', 'name', 'config', 'cssPlus', 'cssMinus'];
  const DEFAULT_TAB = '99.Other';

  useEffect(() => {
    if (isSendingUpdates) {
      onLivePreviewUpdate({});
      setIsSendingUpdates(false);
    }
  }, [isSendingUpdates, onLivePreviewUpdate]);

  useEffect(() => {
    setPages(_.merge({}, template.pages, currentOverrides.pages || {}));
    setApplets(_.merge({}, template.applets, currentOverrides.applets || {}));
    setBlocks(_.merge({}, template.blocks, currentOverrides.blocks || {}));
    setComponents(_.merge({}, template.components, currentOverrides.components || {}));
  }, [template, currentOverrides]);

  const checkLayoutItemUsage = useCallback((type, name) => {
    const regExp = new RegExp(`"${name}"`, 'g');

    const countOccurrences = (config) => {
      return (JSON.stringify(config).match(regExp) || []).length;
    };

    return {
      pages: countOccurrences(pages) - (type === BUILD_TIME_CONFIG_TYPES.PAGES ? 1 : 0),
      applets: countOccurrences(applets) - (type === BUILD_TIME_CONFIG_TYPES.APPLETS ? 1 : 0),
      blocks: countOccurrences(blocks) - (type === BUILD_TIME_CONFIG_TYPES.BLOCKS ? 1 : 0),
      components: countOccurrences(components) - (type === BUILD_TIME_CONFIG_TYPES.COMPONENTS ? 1 : 0)
    };
  }, [pages, applets, blocks, components]);

  const saveChanges = useCallback(() => {
    if (!_.isEqual(JSON.parse(buildTimeConfig.config['/config/placeholder.json']), currentOverrides)) {
      const result = _.cloneDeep(allConfig);

      result.config['/config/placeholder.json'] = JSON.stringify(currentOverrides);

      onSave(result);
    }
  }, [buildTimeConfig, onSave, currentOverrides, allConfig]);

  const applyToLivePreview = useCallback(() => {
    setIsSendingUpdates(true);
  }, []);

  const sortComponents = useCallback((components) => {
    if (Array.isArray(components)) {
      return [];
    }

    const result = Object.entries(components);

    result.sort(([keyA, configA], [keyB, configB]) => {
      return (Number(configA?.Order || keyA) || 0) - (Number(configB?.Order || keyB) || 0);
    });

    return result;
  }, []);

  const getNestedObject = useCallback((path, obj) => {
    let result = obj;

    path.forEach((key) => {
      if (!result[key]) {
        result[key] = {};
      }

      result = result[key];
    });

    return result;
  }, []);

  const applyHighlighter = useCallback(({ jsxName, editingPath }) => {
    const metadata = templateMetadata.jsxComponents.find(({ name }) => name === jsxName);
    const highlightCssPath = metadata.highlightCssPath;
    const highlightCssClass = metadata.highlightCssClass;

    const arr = [...highlightCssPath];
    arr.pop();
    const key = highlightCssPath[highlightCssPath.length - 1];

    const livePreviewOverrides = _.cloneDeep(currentOverrides);
    const overridesObject = getNestedObject([...editingPath, ...arr], livePreviewOverrides);

    overridesObject[key] = highlightCssClass;

    const result = _.cloneDeep(allConfig);

    result.config['/config/placeholder.json'] = JSON.stringify(livePreviewOverrides);

    onLivePreviewUpdate({ livePreviewConfig: result });
  }, [templateMetadata, onLivePreviewUpdate, currentOverrides, allConfig, getNestedObject]);

  const getSelectedLayoutItemConfig = useCallback((type, name) => {
    switch (type) {
      case BUILD_TIME_CONFIG_TYPES.APPLETS: { return applets[name] || {} }
      case BUILD_TIME_CONFIG_TYPES.BLOCKS: { return blocks[name] || {} }
      case BUILD_TIME_CONFIG_TYPES.COMPONENTS:
      default: {
        return components[name] || {};
      }
    }
  }, [components, blocks, applets]);

  const getMergedTemplate = useCallback((path, overrideData) => {
    let currentBc;
    let bc;
    let withinBlockName;
    let withinLayoutItemName;
    const updatedPath = [...path];
    let result = _.merge({}, template, overrideData);

    path.forEach((key, i) => {
      if (!result[key]) {
        result[key] = {};
      }

      const isNestedLayoutItem = result[key]?.path === 'LayoutItem' || (typeof result[key]?.name === 'string' && result[key]?.name.length > 0);
      const isPageLayoutItem = path[i - 2] === 'sections';
      if (key === 'LayoutItem' || isNestedLayoutItem || isPageLayoutItem) {
        const name = result[key].name;
        const block = blocks[name];
        const nestedLayout = applets[name] || block || components[name];

        withinLayoutItemName = name;
        if (block) {
          withinBlockName = name;
        }

        result[key].config = _.merge({}, nestedLayout, result[key].config);
      }

      result = result[key];

      if (isNestedLayoutItem && i === path.length - 1) {
        updatedPath.push('config');
      }

      if (!currentBc && i === path.length - 1) {
        currentBc = result.bc;
      }

      if (!bc && i < path.length - 1) {
        bc = result.bc;
      }
    });

    return {
      config: result,
      bc,
      currentBc,
      withinBlockName,
      withinLayoutItemName,
      path: updatedPath,
    };
  }, [applets, blocks, components, template]);

  const getDataByType = useCallback((type) => {
    switch (type) {
      case BUILD_TIME_CONFIG_TYPES.APPLETS:
        return applets;
      case BUILD_TIME_CONFIG_TYPES.BLOCKS:
        return blocks;
      case BUILD_TIME_CONFIG_TYPES.COMPONENTS:
      default:
        return components;
    }
  }, [applets, blocks, components]);

  const getFilteredData = useCallback((type, filter, allowedGroups) => {
    let data = Object.entries(getDataByType(type));

    if (allowedGroups && allowedGroups.length > 0) {
      data = data.filter(([_name, { path, }]) => {
        const metadata = templateMetadata.jsxComponents.find(({ name }) => name === path);
        if (metadata?.groups?.length > 0) {
          return metadata.groups.filter((group) => allowedGroups.includes(group)).length > 0;
        }

        return false;
      });
    }

    if (filter) {
      return data.filter(([_name, { path, }]) => path === filter).map(([name]) => ({ _id: name, name, }));
    }

    return data.map(([name]) => ({ _id: name, name, }));
  }, [getDataByType, templateMetadata.jsxComponents]);

  const getPropOverridesForLayout = useCallback((path, layoutItemType, layoutItemName) => {
    let { config } = getMergedTemplate(path, {});

    const layoutItem = getSelectedLayoutItemConfig(layoutItemType, layoutItemName);
    const newPropConfigOverrides = getNestedObject(path.slice(0, path.length), {});

    return _.merge({}, layoutItem, newPropConfigOverrides, config);
  }, [getSelectedLayoutItemConfig, getNestedObject, getMergedTemplate]);

  useEffect(() => {
    if (propEditorPath.length > 0) {
      const layoutItem = getSelectedLayoutItemConfig(drillDownLayoutItemType, drillDownLayoutItem);
      let { config, bc, currentBc, withinBlockName, withinLayoutItemName } = getMergedTemplate(propEditorPath, currentOverrides);

      if (currentBc) {
        setCurrentBcContext(bcFields[currentBc.substr(1, currentBc.length - 2)]);
      }
      else {
        setCurrentBcContext(null);
      }

      if (bc) {
        setOuterBc(bc.substr(1, bc.length - 2));
        setHasTemplateBc(true);
      }
      else if ((!state || !state.keepBcContext) && hasTemplateBc) {
        setHasTemplateBc(false);
        setOuterBc(null);
        setBcContext(null);
      }
      const newPropConfigOverrides = getNestedObject(propEditorPath.slice(0, propEditorPath.length), currentOverrides);
      setPropConfigOverrides(newPropConfigOverrides);
      setPropOverrides(_.merge({}, layoutItem, newPropConfigOverrides, config, { BLOCK: withinBlockName, LAYOUTITEM: withinLayoutItemName }));
    }
  }, [propEditorPath, currentOverrides, bcFields, drillDownLayoutItemType, drillDownLayoutItem, getNestedObject, getMergedTemplate, getSelectedLayoutItemConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (outerBc) {
      setBcContext(bcFields[outerBc]);
    }
  }, [outerBc, bcFields]);

  const add = useCallback(async (path, value, creatingPage = false, customKey) => {
    const newOverrides = await new Promise((resolve) => {
      setCurrentOverrides((overrideData) => {
        const overridesObject = getNestedObject(creatingPage ? path.slice(0, -1) : path, overrideData);

        if (creatingPage) {
          overridesObject[path.at(-1)] = value;
        }
        else {
          const { config } = getMergedTemplate(path, overrideData);
          const configKeys = Object.keys(config).map((key) => Number(key) || 0).sort(Utils.numberCompareFn);
          const orderKeys = configKeys.map((key) => Number(config[key]?.Order || key) || 0).sort(Utils.numberCompareFn);
          const newKey = (configKeys.at(-1) || 0) + 100;
          const newOrderKey = (orderKeys.at(-1) || 0) + 100;
          value.Order = newOrderKey;
          overridesObject[customKey || newKey] = value;
        }
        resolve(getNestedObject(creatingPage ? path.slice(0, -1) : path.slice(0, propEditorPath.length), overrideData));

        return _.cloneDeep(overrideData);
      });
    });

    if (!creatingPage) {
      setPropConfigOverrides(newOverrides);
    }
    else {
      setPropConfigOverrides(null);
    }
    saveChanges();
  }, [propEditorPath, saveChanges, getMergedTemplate, getNestedObject]);

  const remove = useCallback(async (path, key, nullify = false) => {
    const newOverrides = await new Promise((resolve) => {
      setCurrentOverrides((overrideData) => {
        const overridesObject = getNestedObject(path, overrideData);

        // * removes new keys and clears overrides from template keys
        let order = '';
        if (overridesObject[key] || overridesObject[key] === '') {
          order = overridesObject[key].Order;
          delete overridesObject[key];
        }

        // * if the key still exists, it is part of the template so needs to be explicitly hidden
        const { config } = getMergedTemplate(path, overrideData);
        if (config[key] || config[key] === '') {
          // * want to nullify in the case of LayoutItem types
          if (nullify) {
            overridesObject[key] = null;
          }
          else {
            overridesObject[key] = overridesObject[key] || {};
            overridesObject[key].Order = order;
            overridesObject[key].Hidden = true;
          }
        }
        resolve(getNestedObject(path.slice(0, propEditorPath.length), overrideData));

        return _.cloneDeep(overrideData);
      });
    });

    setPropConfigOverrides(newOverrides);
    saveChanges();
  }, [propEditorPath, saveChanges, getNestedObject, getMergedTemplate]);

  const reset = useCallback(async (path, key) => {
    const newOverrides = await new Promise((resolve) => {
      setCurrentOverrides((overrideData) => {
        const overridesObject = getNestedObject(path, overrideData);

        // * removes new keys and clears overrides from template keys
        if (overridesObject[key] || overridesObject[key] === '') {
          delete overridesObject[key];
        }

        resolve(getNestedObject(path.slice(0, propEditorPath.length), overrideData));
        return _.cloneDeep(overrideData);
      });
    });

    setPropConfigOverrides(newOverrides);
    saveChanges();
  }, [propEditorPath, saveChanges, getNestedObject]);

  const edit = useCallback(async (path, key, value) => {
    const newOverrides = await new Promise((resolve) => {
      setCurrentOverrides((overrideData) => {
        const overridesObject = getNestedObject(path, overrideData);

        overridesObject[key] = value;

        resolve(getNestedObject(path.slice(0, propEditorPath.length), overrideData));

        return _.cloneDeep(overrideData);
      });
    });

    setPropConfigOverrides(newOverrides);
    saveChanges();
  }, [propEditorPath, saveChanges, getNestedObject]);

  const swapOrder = (overridesObject, key, order) => {
    overridesObject[key] = overridesObject[key] || {};
    overridesObject[key].Order = order;
  };

  const reorder = useCallback(async (path, oldIndex, newIndex) => {
    const newOverrides = await new Promise((resolve) => {
      setCurrentOverrides((overrideData) => {
        const overridesObject = getNestedObject(path, overrideData);
        const { config } = getMergedTemplate(path, overrideData);

        const components = sortComponents(config);

        const keys = components.map(([key]) => key);
        const orders = components.map(([key, { Order, }]) => Number(Order || key) || 0);

        if (oldIndex < newIndex) {
          for (let i = oldIndex; i < newIndex; i += 1) {
            const entry = Object.entries(overridesObject).find(([, value]) => value.Order === orders[i]);
            const entry2 = Object.entries(overridesObject).find(([, value]) => value.Order === orders[i + 1]);

            const entryKey = entry ? entry[0] : keys[i];
            const entryKey2 = entry2 ? entry2[0] : keys[i + 1];

            swapOrder(overridesObject, entryKey, orders[i + 1]);
            swapOrder(overridesObject, entryKey2, orders[i]);

          }
        }
        else {
          for (let i = oldIndex; i > newIndex; i -= 1) {
            const entry = Object.entries(overridesObject).find(([, value]) => value.Order === orders[i]);
            const entry2 = Object.entries(overridesObject).find(([, value]) => value.Order === orders[i - 1]);

            const entryKey = entry ? entry[0] : keys[i];
            const entryKey2 = entry2 ? entry2[0] : keys[i - 1];
            swapOrder(overridesObject, entryKey, orders[i - 1]);
            swapOrder(overridesObject, entryKey2, orders[i]);

          }
        }
        resolve(path.slice(0, propEditorPath.length), overrideData);

        return _.cloneDeep(overrideData);
      });
    });

    setPropConfigOverrides(newOverrides);
    saveChanges();
  }, [propEditorPath, saveChanges, getNestedObject, getMergedTemplate, sortComponents]);

  const selectLayoutItem = useCallback(({ type, name = null, templateOverrides = {} }) => {
    setSelectedLayoutItemType(type);
    setSelectedLayoutItem(name);
    setDrillDownLayoutItemType(type);
    setDrillDownLayoutItem(name);

    setPropConfigOverrides(templateOverrides);
  }, []);

  const selectDrillDownLayoutItem = useCallback(({ type, name, templateOverrides }) => {
    setDrillDownLayoutItemType(type);
    setDrillDownLayoutItem(name);

    setPropConfigOverrides(templateOverrides);
  }, []);

  const resetSelectedLayoutItem = useCallback(() => {
    setSelectedLayoutItem(null);
    setSelectedLayoutItemType(null);
    setDrillDownLayoutItem(null);
    setDrillDownLayoutItemType(null);

    setPropEditorPath([]);
    setPropOverrides(null);

    setBcContext(null);
    setOuterBc(null);
    setHasTemplateBc(false);
  }, []);

  const navigateToLayout = useCallback(({ history, layoutType, layoutName, query, state }) => {
    const [objectType, id] = window.location.pathname.slice(1).split('/');
    history.push({
      pathname: `/${objectType}/${id}/${layoutType}${layoutName ? `/${layoutName}` : '/'}`,
      search: query ? `?${query}` : '',
      state
    });
  }, []);

  const getInitialValue = (configValue, defaultValue, type) => {
    let initialValue = {};

    if (configValue === null || configValue === '') {
      return configValue;
    }

    if (['Object', 'LayoutItem', 'SortableObject', 'CSS'].includes(type)) {
      return _.merge(initialValue, defaultValue || {}, configValue || {});
    }

    return configValue || defaultValue;
  }

  const getOverridesValue = (overridesValue, keyName, configKey) => {
    if (!overridesValue) {
      return undefined;
    }

    if (configKey) {
      if (!overridesValue[configKey]) {
        return undefined;
      }

      if (keyName === 'css') {
        return overridesValue[configKey]['cssPlus'] || overridesValue[configKey]['cssMinus'];
      }

      return overridesValue[configKey][keyName];
    }

    if (keyName === 'css') {
      return overridesValue['cssPlus'] || overridesValue['cssMinus'];
    }

    return overridesValue[keyName];
  }

  const renderComponentForParameter = useCallback(({ param, index, disabled, currentPath, configKey, updateCallback, resetCallback }) => {
    const newPath = configKey ? [...currentPath, configKey, param.name] : [...currentPath, param.name];

    const textStyle = () => {
      if (param.defaultValue && param.overridesValue === undefined && param.initialValue === param.defaultValue) {
        return 'text-primary';
      }

      return '';
    }

    switch (param.type) {
      case 'String': {
        return <GenericTextBox
          label={param.friendlyName || param.name}
          tooltip={param.description}
          type='text'
          css={{ input: `form-control-sm ${textStyle()}` }}
          readOnly={disabled}
          defaultValue={param.initialValue}
          onBlur={(event) => updateCallback(configKey, param.name, event.target.value, newPath)}
          key={`${index}.${param.initialValue}`}
        />
      }
      case 'Boolean': {
        return <GenericCheckbox
          id={param.name}
          tooltip={param.description}
          label={param.friendlyName || param.name}
          initialValue={param.initialValue}
          onChange={(value) => updateCallback(configKey, param.name, value)}
          disabled={disabled}
          key={`${index}.${param.initialValue}`}
        />
      }
      case 'Number': {
        return <GenericTextBox
          label={param.friendlyName || param.name}
          tooltip={param.description}
          type='number'
          css={{ input: `form-control-sm ${textStyle()}` }}
          readOnly={disabled}
          defaultValue={param.initialValue}
          onBlur={(event) => updateCallback(configKey, param.name, event.target.value)}
          key={`${index}.${param.initialValue}`}
        />
      }
      case 'Icon': {
        return <GenericTextBox
          label={param.friendlyName || param.name}
          tooltip={param.description}
          type='text'
          css={{ input: `form-control-sm ${textStyle()}` }}
          readOnly={disabled}
          defaultValue={param.initialValue}
          onBlur={(event) => updateCallback(configKey, param.name, event.target.value)}
          key={`${index}.${param.initialValue}`}
        />
      }
      case 'Function': {
        return <div className="row" key={`${index}.${param.initialValue}`}>
          <div className='col'>
            <GenericTextBox
              label={param.friendlyName || param.name}
              tooltip={param.description}
              type='text'
              css={{ input: `form-control-sm ${textStyle()}` }}
              readOnly={disabled}
              defaultValue={param.initialValue}
              onBlur={(event) => updateCallback(configKey, param.name, event.target.value)}
            />
          </div>
          <div className="col-auto mt-auto">
            <Button
              size="sm"
              disabled={disabled}
              onClick={() => {
                setValuePrompterData({
                  show: true,
                  configKey,
                  paramName: param.name,
                  displayName: param.friendlyName || param.name,
                  description: param.description,
                  initialValue: param.initialValue,
                  overridesValue: param.overridesValue,
                  updateCallback,
                  resetCallback
                })
              }}
            >
              {translate("OTM_QUICK_FILL")}
            </Button>
          </div>
        </div>
      }
      case 'BC': {
        return <GenericDropdown
          heading={param.friendlyName || param.name}
          tooltip={param.description}
          options={Object.keys(bcs).map((bc) => ({ _id: `'${bc}'`, name: bc, }))}
          valueKey='_id'
          size='sm'
          displayValueKey='name'
          defaultValue={param.initialValue}
          onSelect={(value) => updateCallback(configKey, param.name, value)}
          readOnly={disabled}
          grow={true}
          key={`${index}.${param.initialValue}`}
        />
      }
      case 'CurrentContextObservable': {
        if (currentBcContext) {
          let autoComplete = null;
          if (!disabled) {
            const options = [];
            currentBcContext.forEach((attributeName) => {
              options.push({ value: `${attributeName}.RawValue()`, displayValue: `${attributeName}`, });
              options.push({ value: `${attributeName}.Value()`, displayValue: `${attributeName} (Read Only)`, });
            });

            if (options.length) {
              autoComplete = {
                placeholder: '--Fields--',
                size: 'sm',
                options,
                displaySelectedValue: false,
                onSelect: (currentValue, value) => updateCallback(configKey, param.name, `${currentValue || ''}${value}`)
              }
            }
          }

          return <GenericTextBox
            label={param.friendlyName || param.name}
            tooltip={param.description}
            type='text'
            css={{ input: `form-control-sm ${textStyle()}` }}
            readOnly={disabled}
            defaultValue={param.initialValue}
            onBlur={(event) => updateCallback(configKey, param.name, event.target.value)}
            autoComplete={autoComplete}
            key={`${index}.${param.initialValue}`}
          />
        }

        return <GenericTextBox
          label={param.friendlyName || param.name}
          tooltip={param.description}
          type='text'
          css={{ input: `form-control-sm ${textStyle()}` }}
          readOnly={disabled}
          defaultValue={param.initialValue}
          onBlur={(event) => updateCallback(configKey, param.name, event.target.value)}
          key={`${index}.${param.initialValue}`}
        />
      }
      case 'Observable': {
        return <div className="row" key={`${index}.${param.initialValue}`}>
          <div className='col'>
            <GenericTextBox
              label={param.friendlyName || param.name}
              tooltip={param.description}
              type='text'
              css={{ input: `form-control-sm ${textStyle()}` }}
              readOnly={disabled}
              defaultValue={param.initialValue}
              onBlur={(event) => updateCallback(configKey, param.name, event.target.value)}
            />
          </div>
          <div className="col-auto mt-auto">
            <Button
              size="sm"
              disabled={disabled}
              onClick={() => {
                setValuePrompterData({
                  show: true,
                  configKey,
                  paramName: param.name,
                  displayName: param.friendlyName || param.name,
                  description: param.description,
                  initialValue: param.initialValue,
                  overridesValue: param.overridesValue,
                  updateCallback,
                  resetCallback
                })
              }}
            >
              {translate("OTM_QUICK_FILL")}
            </Button>
          </div>
        </div>
      }
      case 'LayoutItem': {
        return <LayoutItemListRenderer
          singleItem={true}
          name={param.name}
          label={param.friendlyName || param.name}
          description={param.description}
          allowedGroups={param.allowedGroups}
          path={newPath}
          initialValue={param.initialValue}
          disabled={disabled}
          key={`${index}.${param.initialValue}`}
        />
      }
      case 'LayoutItemList': {
        return <LayoutItemListRenderer
          name={param.name}
          label={param.friendlyName || param.name}
          description={param.description}
          allowedGroups={param.allowedGroups}
          path={newPath}
          initialValue={param.initialValue}
          disabled={disabled}
          key={`${index}.${param.initialValue}`}
        />
      }
      case 'CSS': {
        return <CssComponent
          label={param.friendlyName || param.name}
          description={param.description}
          keys={param.keys}
          disabled={disabled}
          initialValue={param.initialValue}
          initialCssPlus={param.initialCssPlus}
          initialCssMinus={param.initialCssMinus}
          overridesValue={param.overridesValue}
          path={newPath}
          key={`${index}.${param.initialValue}`}
        />
      }
      case 'Object': {
        return <ObjectComponent
          label={param.friendlyName || param.name}
          description={param.description}
          keys={param.keys}
          disabled={disabled}
          initialValue={param.initialValue}
          overridesValue={param.overridesValue}
          path={newPath}
          onChange={updateCallback}
          onReset={resetCallback}
          key={`${index}.${param.initialValue}`}
        />
      }
      case 'SortableObject': {
        return <SortableObjectComponent
          label={param.friendlyName || param.name}
          description={param.description}
          keys={param.keys}
          path={newPath}
          initialValue={param.initialValue}
          overridesValue={param.overridesValue}
          disabled={disabled}
          key={`${index}.${param.initialValue}`}
        />
      }
      case 'CustomKeyObject': {
        return <CustomKeyObject
          label={param.friendlyName || param.name}
          description={param.description}
          keys={param.keys}
          path={newPath}
          initialValue={param.initialValue}
          overridesValue={param.overridesValue}
          disabled={disabled}
          key={`${index}.${param.initialValue}`}
        />
      }
      default: {
        console.warn(`Configure does not support parmeter type: ${param.type}`);
        return;
      }
    }
  }, [currentBcContext, bcs, translate]);

  const translationFiles = useCallback(() => {
    const baseFilePath = '/l10n/';
    var files = [];

    if (templateConfig && templateConfig['languages']) {
      files = Object.keys(templateConfig['languages']).map(el =>
        el.replace(baseFilePath, '').replace('.json', '')
      )
    }

    if (buildTimeConfig && buildTimeConfig['languages']) {
      Object.keys(buildTimeConfig['languages']).forEach(el => {
        const file = el.replace(baseFilePath, '').replace('.json', '');
        if (!files.includes(file)) {
          files.push(file);
        }
      }

      )
    }
    return files;
  }, [buildTimeConfig, templateConfig]);

  const translationsForFile = useCallback((fileName) => {
    const filePath = '/l10n/' + fileName + '.json';
    var mergedConfig = {};
    if (!templateConfig || !templateConfig['languages']) {
      return [];
    }
    if (!buildTimeConfig || !buildTimeConfig['languages']) {
      return [];
    }
    if (templateConfig['languages'][filePath]) {
      mergedConfig = _.clone(templateConfig['languages'][filePath]);
    }

    if (buildTimeConfig['languages'][filePath]) {
      Object.entries(buildTimeConfig['languages'][filePath]).forEach((entry) => {
        mergedConfig[entry[0]] = entry[1];
      });
    }

    return Object.entries(mergedConfig).map((entry) => (
      {
        value: `'${entry[0]}'`,
        displayValue: `[${entry[0]}]: ${entry[1]}`
      }
    ));
  }, [buildTimeConfig, templateConfig]);

  return (
    <UIConfigContext.Provider value={{
      pages,
      applets,
      blocks,
      components,
      pageHeader: templateMetadata.pageHeader,
      pageFooter: templateMetadata.pageFooter,
      add,
      remove,
      edit,
      reset,
      reorder,
      basePath,
      setBasePath,
      propEditorPath,
      setPropEditorPath,
      selectLayoutItem,
      selectDrillDownLayoutItem,
      propOverrides,
      propConfigOverrides,
      selectedLayoutItem,
      selectedLayoutItemType,
      drillDownLayoutItem,
      drillDownLayoutItemType,
      getFilteredData,
      currentOverrides,
      getMergedTemplate,
      sortComponents,
      bcs,
      bcContext,
      outerBc,
      setOuterBc,
      hasTemplateBc,
      jsxComponents: templateMetadata.jsxComponents,
      layouts: templateMetadata.layouts,
      reservedArgs,
      canSendUpdate,
      saveChanges,
      applyToLivePreview,
      renderComponentForParameter,
      resetSelectedLayoutItem,
      navigateToLayout,
      checkLayoutItemUsage,
      getInitialValue,
      getOverridesValue,
      DEFAULT_TAB,
      showAdvancedProps,
      setShowAdvancedProps,
      getPropOverridesForLayout,
      applyHighlighter,
      valuePrompterData,
      setValuePrompterData,
      translationFiles,
      translationsForFile,
      prompterFunctions: templateMetadata.prompterFunctions,
      bcFields
    }}>
      {children}
    </UIConfigContext.Provider>
  );
}

export function useUIConfig() {
  return useContext(UIConfigContext);
}